<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractTerms',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const filterItems = (...args) => {
    return _.sortBy(args[0], (p) => (isTermStale(p) ? '_' + p.name : p.name));
  };
  const isTermStale = (...args) => {
    const [term] = args;
    return term.valid_to && $fn.value.parseISODate(term.valid_to) < today.value;
  };

  const today = ref(null);
  const displayHelper = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    today: today.value,
    displayHelper: displayHelper.value,
    ux: ux.value,
  }));

  const $$load_today = async () => {
    return new Date();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'today' }, { name: 'displayHelper' }, { name: 'ux' }],
    loaders: { today: $$load_today },
    ux: {},
    dataRefs: { today, displayHelper, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_hl6pn9mwm_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_38xf5dems_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2095089031469157_showModal_3191rlvfl_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractTermEditor',
      props: {
        mode: 'add',
        contract: props.contract,
      },
    };
  };
  const act_3577443900630089_emit_ad178atci_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_3577443900630089_close_ey8hb7i7u_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_3577443900630089_showModal_x13t4fzp8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractTermEditor',
      props: {
        mode: 'edit',
        contract: props.contract,
        term: _.cloneDeep($event.scope.term),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_e9ltuiqze =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_jpx4wn3i5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e9ltuiqze,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_jpx4wn3i5],
    event: 'reload',
  });
  const act_2095089031469157_emit_hl6pn9mwm = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_hl6pn9mwm_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_2095089031469157_close_38xf5dems =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_hl6pn9mwm,
      event: 'close',
      when: inst_evh_2095089031469157_close_38xf5dems_getWhen,
      displayName: 'emitReload',
    });
  const act_2095089031469157_showModal_3191rlvfl =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_3191rlvfl_getActionArgs,
      events: [inst_evh_2095089031469157_close_38xf5dems],
    });
  const inst_evh_2095089031469157_clickPreventStop_5x0aigd8s =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_3191rlvfl,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_5x0aigd8s],
      event: 'click.prevent.stop',
    });
  const act_3577443900630089_emit_ad178atci = new actionTypes.EmitAction({
    actionArgs: act_3577443900630089_emit_ad178atci_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_3577443900630089_close_ey8hb7i7u =
    new actionTypes.EventHandlerImpl({
      action: act_3577443900630089_emit_ad178atci,
      event: 'close',
      when: inst_evh_3577443900630089_close_ey8hb7i7u_getWhen,
      displayName: 'emitReload',
    });
  const act_3577443900630089_showModal_x13t4fzp8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_3577443900630089_showModal_x13t4fzp8_getActionArgs,
      events: [inst_evh_3577443900630089_close_ey8hb7i7u],
    });
  const inst_evh_3577443900630089_clickPreventStop_ad193q0rq =
    new actionTypes.EventHandlerImpl({
      action: act_3577443900630089_showModal_x13t4fzp8,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_3577443900630089_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3577443900630089_clickPreventStop_ad193q0rq],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_jpx4wn3i5,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_38xf5dems,
    emitReload: inst_evh_2095089031469157_close_38xf5dems,
    inst_evh_2095089031469157_clickPreventStop_5x0aigd8s,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_3577443900630089_close_ey8hb7i7u,
    inst_evh_3577443900630089_clickPreventStop_ad193q0rq,
    inst_evh_3577443900630089_clickPreventStop,
  };

  const evh_7315092382398562_reload_jpx4wn3i5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_jpx4wn3i5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_38xf5dems = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_38xf5dems.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_5x0aigd8s = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_5x0aigd8s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3577443900630089_close_ey8hb7i7u = async (event, scope) => {
    return await actions.inst_evh_3577443900630089_close_ey8hb7i7u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3577443900630089_clickPreventStop_ad193q0rq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3577443900630089_clickPreventStop_ad193q0rq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3577443900630089_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3577443900630089_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'Terms') }}
          </strong>
          <ItpButton
            :text="pgettext('Contract', 'Add term...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!contract.terms || !contract.terms.length">
          <ItpText>
            {{ pgettext('Contract', 'No terms have been added yet.') }}
          </ItpText>
        </BListGroupItem>
        <BListGroupItem
          :key="term.id"
          v-for="term in filterItems(contract.terms)"
        >
          <ItpBox alignItems="center">
            <ItpText
              :text="term.name"
              :strikethrough="isTermStale(term)"
              class="fw-bold"
            >
            </ItpText>
            <ItpIcon
              :title="gettext('Locked')"
              name="fa-lock"
              class="px-3"
              v-if="term.locked"
            >
            </ItpIcon>
            <div>
              {{ date(term.valid_from) }}
              <span v-if="term.valid_from && term.valid_to">-</span>
              {{ date(term.valid_to) }}
            </div>
            <ItpBox>
              <ItpText>
                <ItpText class="px-3">
                  <span v-if="term.quantity != null">
                    {{ number(term.quantity) }} x
                  </span>
                  <span v-if="term.amount != null">
                    {{ number(term.amount) }}
                  </span>
                  <span v-if="term.currency">
                    {{ term.currency }}
                  </span>
                  <span v-if="term.unit"> / {{ term.unit }} </span>
                </ItpText>
              </ItpText>
              <ItpText :text="term.notes" pre v-if="term.notes"> </ItpText>
            </ItpBox>
            <ItpButton
              icon="fa-pen"
              :tooltip="gettext('Edit')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_3577443900630089_clickPreventStop($event, { term })
              "
            >
            </ItpButton>
          </ItpBox>
          <ItpBox>
            <ItpText :text="term.description" pre> </ItpText>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
